import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Inject, Injectable, Injector } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { ToastService } from '../services/toast.service';
import { Router } from '@angular/router';
import { ERROR_MESSAGES } from './error-messages';
import { ElasticLoggerService } from '../services/elastic-logger.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly elasticLoggerService = inject(ElasticLoggerService);
  isDuplicateUser = false;

  constructor(
    @Inject(Injector) private readonly injector: Injector,
    private readonly router: Router
  ) { }

  private get toastService(): ToastService {
    return this.injector.get(ToastService);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        let errorTitle = 'Internal Server Error';
        let errorMessage = ERROR_MESSAGES[500];

        if (error.error?.error?.message) {
            this.isDuplicateUser = error.error?.error?.message.toLowerCase().includes('already taken');
            errorMessage = error.error.error.message;
        }
        // Check if ABP-style error exists
        else if (error.error?.result?.error?.message) {
          // New scenario: ABP error format
          errorMessage = error.error.result.error.message;
        }
        else if (error.error?.result?.errors) {
          errorMessage = '';
          // Existing scenario: Non-ABP error with message
          for (const key in error.error.result.errors) {
            errorMessage += error.error.result.errors[key] + ' ';
          }
        }
        else if (error.error?.error?.validationErrors) {
          errorMessage = '';
          // Existing scenario: Non-ABP error with message
          for (const key in error.error.error.validationErrors) {
            errorMessage += error.error.error.validationErrors[key]?.message + ' ';
          }
        }
        else {
          // Existing logic remains unchanged
          ({ errorTitle, errorMessage } = this.ErrorHandler(error, errorTitle, errorMessage));
        }

        this.elasticLoggerService.error({
          service: 'ErrorInterceptor',
          message: errorMessage,
          details: {
            title: errorTitle,
            status: error.status,
            url: request.urlWithParams,
            method: request.method,
            original: error.error,
          }
        });

        const isDuplicateCase =
          error.error?.result?.error?.code === 'FIELD_CUSTOM_VALIDATION_EXCEPTION' &&
          error.error?.result?.error?.message
            ?.toLowerCase()
            .includes('already been opened');

        // Hide the generic error toast for duplicate cases & users.
        // The component handles the duplicate-case notification.
        if (!isDuplicateCase && !this.isDuplicateUser) {
          this.toastService.error(
            errorMessage,
            `${error.status !== 0 ? error.status : ''} ${errorTitle}`
          );
        }

        return throwError(() => error);
      })
    );
  }

  private ErrorHandler(error: HttpErrorResponse, errorTitle: string, errorMessage: string) {
    if (error.error instanceof ErrorEvent) {
      errorTitle = 'Client side error';
      errorMessage = error.error?.message;
    } else {
      switch (error.status) {
        case HttpStatusCode.BadRequest:
          if (error.error?.result?.errors) {
            const modelStateErrors: any = [];
            for (const key in error.error.result.errors) {
              modelStateErrors.push(error.error.result.errors[key]);
            }
            errorMessage = modelStateErrors.join(' ');
          } else {
            errorMessage = ERROR_MESSAGES[HttpStatusCode.BadRequest];
          }
          errorTitle = 'Bad Request';
          break;
        case HttpStatusCode.Unauthorized:
          errorTitle = 'Unauthorized';
          errorMessage = ERROR_MESSAGES[HttpStatusCode.Unauthorized];
          this.router.navigateByUrl('/account/login');
          break;
        case HttpStatusCode.Forbidden:
          errorTitle = 'No Permissions Found';
          errorMessage = ERROR_MESSAGES[HttpStatusCode.Forbidden];
          break;
        case HttpStatusCode.NotFound:
          errorTitle = 'Not Found';
          errorMessage = ERROR_MESSAGES[HttpStatusCode.NotFound];
          break;
        case 0: // unidentifed error or no internet
          errorTitle = 'Request Failed';
          errorMessage = ERROR_MESSAGES[0];
          break;
        default:
          errorMessage = ERROR_MESSAGES[500];
          break;
      }
    }
    return { errorTitle, errorMessage };
  }
}
